import { createRoot } from 'react-dom/client'
import RadioPlayer from './components/RadioPlayer'
import type { PlayerConfig } from './components/samApi'

function hasMarketingConsent(): boolean {
  return window.cookiehub?.hasConsented('marketing') ?? false
}

// Swap the media-defer placeholder for the (hidden) player container. Normally
// mediaDefer.ts does this on placeholder click, but when consent is granted via
// the global cookie banner we reveal it ourselves.
function revealContent(defer: HTMLElement): void {
  const content = defer.querySelector<HTMLElement>('.media-defer__content')
  const placeholder = defer.querySelector<HTMLElement>(
    '.media-defer__placeholder'
  )
  if (content) content.style.display = 'block'
  if (placeholder) placeholder.style.display = 'none'
}

function setup(container: HTMLElement): void {
  const configJSON = container.dataset.radioPlayerConfig
  if (!configJSON) {
    console.warn('Missing data-radio-player-config attribute')
    return
  }
  const config = JSON.parse(configJSON) as PlayerConfig
  const defer = container.closest<HTMLElement>('.media-defer')

  let mounted = false
  function mount(autoPlay: boolean): void {
    if (mounted) return
    mounted = true
    if (defer) revealContent(defer)
    createRoot(container).render(
      <RadioPlayer config={config} autoPlay={autoPlay} />
    )
    document.removeEventListener('click', onDocumentClick)
  }

  // Re-check after any click so granting consent in the global cookie banner
  // loads the player too — without autoplay, since there was no play intent.
  function onDocumentClick(): void {
    setTimeout(() => {
      if (hasMarketingConsent()) mount(false)
    }, 0)
  }

  // No gate (editor preview) or consent already given → mount straight away.
  if (!defer || hasMarketingConsent()) {
    mount(false)
    return
  }

  // Clicking the placeholder grants consent via mediaDefer and implies a play
  // intent → autoplay.
  defer.addEventListener('media-defer:replaced', () => mount(true), {
    once: true
  })
  document.addEventListener('click', onDocumentClick)
}

for (const container of document.querySelectorAll<HTMLElement>(
  '[data-radio-player-config]'
)) {
  setup(container)
}
